import React, { useEffect } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store, RootState } from './store';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider, CssBaseline, createTheme } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import TournamentsPage from './pages/TournamentsPage';
import RoomsPage from './pages/RoomsPage';
import TransactionsPage from './pages/TransactionsPage';
import DepositsPage from './pages/DepositsPage';
import WithdrawalsPage from './pages/WithdrawalsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import WinningPatternsPage from './pages/WinningPatternsPage';
import PaymentMethodsPage from './pages/PaymentMethodsPage';
import MediaPage from './pages/MediaPage';
import AdminsPage from './pages/AdminsPage';
import SettingsPage from './pages/SettingsPage';
import AgentRequestsPage from './pages/AgentRequestsPage';
import { fetchAdminProfile } from './store/slices/authSlice';
import Layout from './components/common/Layout';
import { useSocketNotifications } from './hooks/useSocketNotifications';
import { usePushNotifications } from './hooks/usePushNotifications';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
  components: {
    MuiDialog: {
      defaultProps: {
        scroll: 'paper',
      },
      styleOverrides: {
        paper: {
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100% - 32px)',
          margin: 16,
          // Mobile: keep title/actions visible; content scrolls
          '@media (max-width:600px)': {
            margin: 8,
            width: 'calc(100% - 16px) !important',
            maxWidth: 'calc(100% - 16px)',
            maxHeight: 'calc(100dvh - 16px)',
            height: 'auto',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          flexShrink: 0,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          flexShrink: 0,
          flexWrap: 'wrap',
          gap: 8,
          paddingTop: 12,
          paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
          paddingLeft: 16,
          paddingRight: 16,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          backgroundColor: 'inherit',
          '@media (max-width:600px)': {
            flexDirection: 'column-reverse',
            alignItems: 'stretch',
            '& > :not(style) ~ :not(style)': {
              marginLeft: 0,
            },
            '& .MuiButton-root': {
              width: '100%',
              margin: 0,
            },
          },
        },
      },
    },
  },
});

function ProtectedRoute() {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
}

function AuthLoader({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.auth.token);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  
  // Use socket notifications hook (in-app)
  useSocketNotifications(isAuthenticated);
  
  // Use push notifications hook (system tray)
  const pushNotifications = usePushNotifications(isAuthenticated);
  
  useEffect(() => {
    // Refresh role/profile from DB so Admins visibility rules stay correct
    if (token && isAuthenticated) {
      dispatch(fetchAdminProfile() as any);
    }
  }, [token, isAuthenticated, dispatch]);

  // Log push notification status
  useEffect(() => {
    if (isAuthenticated && pushNotifications.isSupported) {
      console.log('Push Notifications:', {
        supported: pushNotifications.isSupported,
        permission: pushNotifications.permission,
        subscribed: pushNotifications.isSubscribed,
      });
    }
  }, [isAuthenticated, pushNotifications]);
  
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<ProtectedRoute />}> {/* Protected routes */}
        <Route element={<Layout />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/tournaments" element={<TournamentsPage />} />
          <Route path="/rooms" element={<RoomsPage />} />
          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/deposits" element={<DepositsPage />} />
          <Route path="/withdrawals" element={<WithdrawalsPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/winning-patterns" element={<WinningPatternsPage />} />
          <Route path="/payment-methods" element={<PaymentMethodsPage />} />
          <Route path="/media" element={<MediaPage />} />
          <Route path="/admins" element={<AdminsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/agent-requests" element={<AgentRequestsPage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <Router>
          <AuthLoader>
            <AppRoutes />
          </AuthLoader>
        </Router>
        <ToastContainer 
          position="top-right" 
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
          style={{
            zIndex: 9999,
          }}
        />
      </ThemeProvider>
    </Provider>
  );
}

export default App;
